import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Theater, ArrowLeft } from 'lucide-react';
import apiClient from '../../api/client';
import PersonaEditor from '../../components/PersonaEditor';
import { ensureConfig } from '../../components/personaConfig';
import type { PersonaConfigData } from '../../components/personaConfig';
import { useToast } from '../../components/ui/toastContext';
import { useConfirm } from '../../components/ui/useConfirm';

// ── Types ──
interface PersonaTemplate {
  id: string;
  name: string;
  description?: string;
  category?: string;
  tags?: string[];
  config: PersonaConfigData;
  is_builtin: boolean;
}

// ── Component ──
export default function PersonaConfig() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [config, setConfig] = useState<PersonaConfigData>(ensureConfig({}));
  const [templates, setTemplates] = useState<PersonaTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    try {
      const [personaRes, tmplRes] = await Promise.all([
        apiClient.get(`/agents/${agentId}/persona`).catch(() => ({ data: {} })),
        apiClient.get('/persona/templates'),
      ]);
      setConfig(ensureConfig(personaRes.data || {}));
      setTemplates(tmplRes.data || []);
    } catch (e) {
      console.error('Failed to load persona:', e);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadAll();
    };
    tick();
  }, [loadAll]);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/agents/${agentId}/persona`, config);
      toast.success('人设配置已保存');
    } catch (e) {
      console.error('Failed to save persona:', e);
      toast.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  const applyTemplate = async (templateId: string) => {
    const tmpl = templates.find(t => t.id === templateId);
    if (!tmpl) return;
    if (!(await confirm({ title: `应用模板「${tmpl.name}」？`, message: '这将覆盖当前人设配置。' }))) return;
    try {
      const res = await apiClient.post(`/agents/${agentId}/persona/apply-template/${templateId}`, {
        merge_strategy: 'replace',
      });
      setConfig(ensureConfig(res.data));
      toast.success(`已应用模板「${tmpl.name}」`);
    } catch (e) {
      console.error('Failed to apply template:', e);
      toast.error('应用模板失败');
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <button className="secondary mb-4" onClick={() => navigate(`/agents/${agentId}`)}>
        <ArrowLeft size={16} /> 返回概览
      </button>

      <div className="card mb-4">
        <h2><Theater size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />人设配置</h2>
        <p>配置 Agent 的角色定位、表达风格、行为边界和交付标准。兼容 agency-agents 9-Section 模板。</p>
      </div>

      <PersonaEditor
        value={config}
        onChange={setConfig}
        onSave={save}
        saving={saving}
        templates={templates}
        onApplyTemplate={applyTemplate}
      />
      {dialog}
    </div>
  );
}
